// AnalyticsView component
// UPDATED: Analytics v3 - single chart layout; mission selector in filters; stats adapt to selected mission
// UPDATED: Aligned with Team page design patterns:
//   - Uses PageHeader for consistent title (no icon, no subtitle)
//   - Uses useAnalyticsFilters hook for persistent site/mission/date state (localStorage, 7-day TTL)
//   - Refresh button moved to filter bar
//   - Site and mission selectors use BaseFilterDropdown (compact) matching Team page style
// UPDATED: Added FrequentationEntries detail table below the chart
// UPDATED: Added Horaire (scheduled time) filter - loads distinct times, passes to all views
// UPDATED: Removed max-w-4xl constraint for full-width layout consistent with other pages
// UPDATED: Removed extra px/py padding - RoleBasedLayout already provides correct padding for all viewports
// UPDATED: Multi-KPI support - missions are aggregated (flatMap + dedup) from all KPI definitions; stats
//          and daily series are summed across all kpis when "Tous les cours" is selected
// FIXED: missionsLoading flag (derived from analyticsLoaded state) prevents the debounced save from firing
//        with lastMissionId:null before KPI data has loaded on first mount

import { useMemo, useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalyticsData } from '../../hooks/useAnalyticsData';
import { useAnalyticsFilters } from '../../hooks/useAnalyticsFilters';
import { AnalyticsFiltersBar } from './AnalyticsFilters';
import { FrequentationGlobalStats } from './FrequentationGlobalStats';
import { FrequentationChart } from './FrequentationChart';
import { FrequentationEntries } from './FrequentationEntries';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';
import { PageHeader } from '../ui/PageHeader';
import { getDistinctScheduledTimes, type MissionOption, type MissionDayPoint } from '../../lib/analytics';

export function AnalyticsView() {
  const { user } = useAuth();
  const [availableMissions, setAvailableMissions] = useState<MissionOption[]>([]);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  // Track whether the first analytics fetch has completed so we don't save prefs
  // with a null mission before the data has had a chance to load (race condition fix).
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);

  const {
    sites,
    selectedSiteId,
    selectedMissionId,
    selectedTime,
    dateFrom,
    dateTo,
    handleSiteChange,
    handleMissionChange,
    handleTimeChange,
    handleDateRangeChange
  } = useAnalyticsFilters({
    userId: user?.id ?? null,
    userRole: user?.role ?? null,
    availableMissions,
    missionsLoading: !analyticsLoaded
  });

  const { kpis, loading, error, reload } = useAnalyticsData({
    dateFrom,
    dateTo,
    siteId: selectedSiteId,
    scheduledTime: selectedTime
  });

  // Mark as loaded once the first fetch completes (stays true through subsequent reloads)
  useEffect(() => {
    if (!loading && !analyticsLoaded) setAnalyticsLoaded(true);
  }, [loading, analyticsLoaded]);

  // Aggregate missions from all KPI definitions, deduplicating by templateId
  useEffect(() => {
    if (kpis.length === 0) {
      setAvailableMissions([]);
      return;
    }
    const seen = new Set<string>();
    const missions: MissionOption[] = [];
    for (const kpi of kpis) {
      for (const m of kpi.missions) {
        if (!seen.has(m.templateId)) {
          seen.add(m.templateId);
          missions.push({ templateId: m.templateId, templateName: m.templateName });
        }
      }
    }
    setAvailableMissions(missions);
  }, [kpis]);

  useEffect(() => {
    let cancelled = false;
    getDistinctScheduledTimes({
      dateFrom,
      dateTo,
      siteId: selectedSiteId,
      templateId: selectedMissionId
    })
      .then(times => { if (!cancelled) setAvailableTimes(times); })
      .catch(() => { if (!cancelled) setAvailableTimes([]); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, selectedSiteId, selectedMissionId]);

  // Find the selected mission across all KPI definitions
  const selectedMission = useMemo(() => {
    if (!selectedMissionId) return null;
    for (const kpi of kpis) {
      const found = kpi.missions.find(m => m.templateId === selectedMissionId);
      if (found) return found;
    }
    return null;
  }, [kpis, selectedMissionId]);

  // Aggregate stats across all KPIs when no mission is selected
  const displayedStats = useMemo(() => {
    if (selectedMission) {
      return {
        total: selectedMission.total,
        sessionCount: selectedMission.sessionCount,
        avgPerSession: selectedMission.avgPerSession
      };
    }
    if (kpis.length === 0) return { total: 0, sessionCount: 0, avgPerSession: 0 };
    const totalSum = kpis.reduce((s, k) => s + k.globalTotal, 0);
    const sessionSum = kpis.reduce((s, k) => s + k.globalSessionCount, 0);
    return {
      total: totalSum,
      sessionCount: sessionSum,
      avgPerSession: sessionSum > 0 ? Math.round((totalSum / sessionSum) * 10) / 10 : 0
    };
  }, [kpis, selectedMission]);

  // Merge daily series across all KPIs when no mission is selected
  const displayedSeries = useMemo((): MissionDayPoint[] => {
    if (selectedMission) return selectedMission.dailySeries;
    if (kpis.length === 0) return [];
    if (kpis.length === 1) return kpis[0].globalDailySeries;
    const dateMap = new Map<string, { total: number; sessionCount: number }>();
    for (const kpi of kpis) {
      for (const p of kpi.globalDailySeries) {
        const existing = dateMap.get(p.date) ?? { total: 0, sessionCount: 0 };
        existing.total += p.total;
        existing.sessionCount += p.sessionCount;
        dateMap.set(p.date, existing);
      }
    }
    return Array.from(dateMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, { total, sessionCount }]) => ({ date, total, sessionCount }));
  }, [kpis, selectedMission]);

  const chartLabel = selectedMission
    ? selectedMission.templateName
    : 'Tous les cours';

  if (!user) return null;

  return (
    <div className="space-y-6">
        <PageHeader title="Statistiques" />

        <AnalyticsFiltersBar
          sites={sites}
          selectedSiteId={selectedSiteId}
          onSiteChange={handleSiteChange}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateRangeChange={handleDateRangeChange}
          missions={availableMissions}
          selectedMissionId={selectedMissionId}
          onMissionChange={handleMissionChange}
          availableTimes={availableTimes}
          selectedTime={selectedTime}
          onTimeChange={handleTimeChange}
          isRefreshing={loading}
          onRefresh={reload}
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <BouncingDotsLoader size="lg" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-sm text-red-700">{error}</p>
            <button
              type="button"
              onClick={reload}
              className="mt-3 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
            >
              Réessayer
            </button>
          </div>
        ) : kpis.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <TrendingUp size={24} className="text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-700 mb-2">Aucun indicateur configuré</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
              Liez un champ numérique à un indicateur depuis la bibliothèque de missions pour commencer à visualiser des données.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <FrequentationGlobalStats
              total={displayedStats.total}
              sessionCount={displayedStats.sessionCount}
              avgPerSession={displayedStats.avgPerSession}
            />
            <FrequentationChart
              dailySeries={displayedSeries}
              missionName={chartLabel}
            />
            <FrequentationEntries
              filters={{
                dateFrom,
                dateTo,
                siteId: selectedSiteId,
                templateId: selectedMissionId,
                scheduledTime: selectedTime
              }}
            />
          </div>
        )}
    </div>
  );
}
